import { Injectable } from '@angular/core';
import { CrossReferenceGraphqlService } from '../../../services/api/cross-reference-graphql.service';
import { CrossreferencesApiService } from '../../../services/api/crossreferences-api.service';
import {
  CrossReference,
  CrossReferenceAssessmentUpdates,
  CrossReferenceNote,
  CrossReferenceOpinionUpdates,
} from '../../../types/product/product';
import { AssessmentGridData } from '../../../types/cross-reference-assessments/assessment-grid-data';
import { CreatedByOrgTypeEnum } from '../../../types/contract/contract-product-cross-reference.enums';

@Injectable({
  providedIn: 'root',
})
export class CrossReferenceAssessmentsService {
  constructor(
    private crossReferenceApi: CrossreferencesApiService,
    private crossReferenceGraphqlService: CrossReferenceGraphqlService,
  ) {}

  updateOpinion(
    crossReferenceId: number,
    fromProductId: number,
    toProductId: number,
    opinionOptionKey: string,
  ) {
    return this.crossReferenceGraphqlService.addCrossReferenceOpinion(
      crossReferenceId,
      fromProductId,
      toProductId,
      opinionOptionKey,
    );
  }

  addNote(
    crossReferenceId: number,
    fromProductId: number,
    toProductId: number,
    noteMessage: string,
  ) {
    return this.crossReferenceGraphqlService.addCrossReferenceNote(
      crossReferenceId,
      fromProductId,
      toProductId,
      noteMessage,
    );
  }

  addCross(fromProductId: number, toProductId: number) {
    return this.crossReferenceApi.addCrosses(fromProductId, toProductId);
  }

  /* @description Create local note object. */
  createNote(email: string, note: string): CrossReferenceNote {
    const date = Date.now();
    return {
      auditInfo: {
        createdDate: date,
        lastModifiedDate: date,
        createdBy: email,
        lastModifiedBy: email,
      },
      note,
    };
  }

  /**
   * Cross-references are optimistically created, so
   * ensure ensure that there is a valid cross reference Id to use for API requests.
   */
  getCrossReferenceIdForApi(
    crossReference?: CrossReference,
    assessmentGridData?: AssessmentGridData,
  ): number {
    if (crossReference?.id) {
      return crossReference?.id;
    } else {
      throw new Error(
        `Cross Reference Id is not available, ${JSON.stringify(
          crossReference,
        )}, crossReferenceId: ${assessmentGridData}`,
      );
    }
  }

  getUpdatedLocalClinicalOpinions(
    crossReference: CrossReference,
    opinionUpdates: CrossReferenceAssessmentUpdates,
    userOrgId: number,
  ): CrossReference {
    crossReference = crossReference ?? ({} as CrossReference);
    // Fill in some blanks until we get the proper data.
    // This allows us to show correct permission state immediately.
    if (!crossReference?.createdByOrg?.id) {
      crossReference.createdByOrg = {
        id: userOrgId,
        name: '',
      };
      crossReference.createdByOrgType = CreatedByOrgTypeEnum.BUYER;
    }

    return Object.assign({}, crossReference, {
      opinion: opinionUpdates.opinion,
      marketplaceApproveCount: opinionUpdates.marketplaceApproveCount,
      marketplaceDisputeCount: opinionUpdates.marketplaceDisputeCount,
    });
  }

  getUpdatedLocalClinicalNotes(
    crossReference: CrossReference,
    noteMessage: string,
    userEmail: string,
  ): CrossReference {
    // Create local note object to optimistically add to our notes array.
    const note = this.createNote(userEmail, noteMessage);

    return Object.assign({}, crossReference, {
      notes: [note, ...(crossReference?.notes || [])],
    });
  }

  getUpdatedLocalVizientOpinions(
    crossReference: CrossReference,
    opinionUpdates: CrossReferenceOpinionUpdates,
    userOrgId: number,
  ): CrossReference {
    crossReference = crossReference ?? ({} as CrossReference);
    // Fill in some blanks until we get the proper data.
    // This allows us to show correct permission state immediately.
    if (!crossReference?.createdByOrg?.id) {
      crossReference.createdByOrg = {
        id: userOrgId,
        name: '',
      };
      crossReference.createdByOrgType = CreatedByOrgTypeEnum.BUYER;
    }

    return Object.assign({}, crossReference, {
      gpoOpinion: opinionUpdates,
    });
  }

  getUpdatedLocalVizientNotes(
    crossReference: CrossReference,
    noteMessage: string,
    userEmail: string,
  ): CrossReference {
    // Create local note object to optimistically add to our notes array.
    const gpoNote = this.createNote(userEmail, noteMessage);

    return Object.assign({}, crossReference, {
      gpoNotes: [gpoNote, ...(crossReference?.gpoNotes || [])],
    });
  }
}
